import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  MessageSquare,
  Zap,
  Clock,
  AlertCircle,
  X
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListAutoRepliesQuery, AutoReplyRule } from '@/client/api/whatsapp/list-auto-replies.query';
import { useCreateAutoReplyMutation } from '@/client/api/whatsapp/create-auto-reply.mutation';
import { useUpdateAutoReplyMutation } from '@/client/api/whatsapp/update-auto-reply.mutation';
import { useDeleteAutoReplyMutation } from '@/client/api/whatsapp/delete-auto-reply.mutation';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';

const triggerTypeConfig: Record<string, { label: string; style: string }> = {
  keyword: { label: 'Keyword', style: 'bg-purple-100 text-purple-700' },
  welcome: { label: 'Welcome', style: 'bg-blue-100 text-blue-700' },
  away: { label: 'Away', style: 'bg-orange-100 text-orange-700' },
};

const AutoRepliesPage: React.FC = () => {
  const { data, isLoading, refetch } = useListAutoRepliesQuery();
  const rules = data?.data?.rules || [];
  const createMutation = useCreateAutoReplyMutation();
  const updateMutation = useUpdateAutoReplyMutation();
  const deleteMutation = useDeleteAutoReplyMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [newType, setNewType] = useState<AutoReplyRule['type']>('keyword');
  const [newTrigger, setNewTrigger] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AutoReplyRule | null>(null);

  const toggleRule = (rule: AutoReplyRule) => {
    updateMutation.mutate(
      { rule_id: rule.rule_id, enabled: !rule.enabled },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to update the rule'),
      }
    );
  };

  const confirmDeleteRule = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { rule_id: deleteTarget.rule_id },
      {
        onSuccess: () => { toast.success('Rule deleted'); setDeleteTarget(null); refetch(); },
      }
    );
  };

  const handleCreate = () => {
    if (!newResponse.trim()) return;
    createMutation.mutate(
      {
        type: newType,
        trigger: newType === 'keyword' ? newTrigger : undefined,
        response: { type: 'text', body: newResponse },
        enabled: true,
      },
      {
        onSuccess: () => {
          toast.success('Auto-reply rule created');
          setShowAddModal(false);
          setNewTrigger('');
          setNewResponse('');
          refetch();
        },
        onError: (error) => toast.error(error.message || 'Failed to create the rule'),
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auto Replies</h1>
            <p className="text-sm text-gray-500 mt-1">Configure automatic responses to incoming WhatsApp messages</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Rules</span>
              <Zap className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{rules.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Active Rules</span>
              <ToggleRight className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{rules.filter(r => r.enabled).length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Inactive Rules</span>
              <ToggleLeft className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{rules.filter(r => !r.enabled).length}</p>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-900 mb-1">Rule Priority</h3>
              <p className="text-sm text-yellow-800">
                Rules run in priority order (lowest number first). The first matching rule wins.
              </p>
            </div>
          </div>
        </div>

        {/* Rules List */}
        {isLoading && <p className="text-sm text-gray-500">Loading...</p>}
        {!isLoading && rules.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No auto-reply rules yet</h3>
            <p className="text-sm text-gray-500">Add a rule to start responding automatically</p>
          </div>
        )}
        <div className="space-y-4">
          {rules.map(rule => {
            const typeCfg = triggerTypeConfig[rule.type];
            return (
              <div key={rule.rule_id} className={`bg-white rounded-lg border ${rule.enabled ? 'border-gray-200' : 'border-gray-200 opacity-60'} p-5`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeCfg?.style}`}>
                          {typeCfg?.label}
                        </span>
                        {!rule.enabled && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            Disabled
                          </span>
                        )}
                      </div>

                      {rule.trigger && (
                        <div className="mb-3">
                          <span className="text-xs font-medium text-gray-500 uppercase">Trigger: </span>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-mono">
                            {rule.trigger}
                          </span>
                        </div>
                      )}

                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-700">{rule.response?.body}</p>
                      </div>

                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Created {moment.unix(rule.created_at).format('YYYY-MM-DD')}
                        </span>
                        <span>Priority: {rule.priority}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => toggleRule(rule)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title={rule.enabled ? 'Disable' : 'Enable'}
                    >
                      {rule.enabled
                        ? <ToggleRight className="w-5 h-5 text-green-500" />
                        : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                    </button>
                    <button onClick={() => setDeleteTarget(rule)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Add Auto-Reply Rule</h2>
              <button onClick={() => setShowAddModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as AutoReplyRule['type'])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="keyword">Keyword match</option>
                  <option value="welcome">Welcome (first message)</option>
                  <option value="away">Away (outside business hours)</option>
                </select>
              </div>
              {newType === 'keyword' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger pattern</label>
                  <input
                    type="text"
                    value={newTrigger}
                    onChange={(e) => setNewTrigger(e.target.value)}
                    placeholder="hi|hello|hey"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Regex pattern matched against the incoming message</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Response</label>
                <textarea
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !newResponse.trim()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Auto-Reply Rule"
        itemLabel={deleteTarget?.trigger || triggerTypeConfig[deleteTarget?.type || '']?.label || 'this rule'}
        onConfirm={confirmDeleteRule}
        isPending={deleteMutation.isPending}
        errorMessage={deleteMutation.error?.message}
      />
    </div>
  );
};

export default AutoRepliesPage;
